import React from "react";
import { useTagsMultiSelect } from "./TagsMultiSelect.hooks";
import DotLegalMultiSelectWithButton from "../dotLegalSelectWithButton/DotLegalMultiSelectWithButton";
import { useTranslation } from "../../../localization/useTranslation";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import TagDialog from "../../../masterData/tag/tagDialog/TagDialog";
import { SelectWrapper } from "../../../taskManagement/taskPane/masterDataSection/MasterDataSection";
import { DotLegalChip, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { Box } from "@mui/material";
import PlanInputFieldWrapper from "../../../plan/planInputFieldWrapper/PlanInputFieldWrapper";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { PurpleLockIcon } from "../../icons";
import { TagRelationType } from "./TagsMultiSelect.types.ts";

export interface TagsMultiSelectProps {
    isLoading: boolean;
    entityType: TagRelationType;
    variant?: "standard" | "outlined";
    disabled?: boolean;
    displayOnlyAsChips?: boolean;
    hideLabel?: boolean;
    noMargin?: boolean;
    selectedTags: Array<string>;
    onChange?: (tags: Array<string>) => void;
    onTagAdded?: (tagId: string) => void;
    onTagRemoved?: (tagId: string) => void;
    placeHolder?: string;
    tooltipText?: string;
    errorText?: string;
    disableQuery?: boolean;
    useFixedLabel?: boolean;
    /** Shown in place of the chips when displayOnlyAsChips has none to show. */
    emptyText?: string;
}

function TagsMultiSelect(props: TagsMultiSelectProps) {
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const { platformFeatures } = usePlanContext();

    const { isLoading, selectableTags, showCreateTagDialog, setShowCreateTagDialog, onTagCreated, onRelationChange } = useTagsMultiSelect(props);

    const chosenTags = selectableTags.filter((x) => props.selectedTags.indexOf(x.id) !== -1);

    const getLoadingBox = () => {
        return (
            <SelectWrapper>
                <DotLegalSelect
                    disabled={!permissions.taskPermissions.edit}
                    options={[]}
                    selectedItem={undefined}
                    disableClearable
                    onChange={() => {}}
                    placeholder={""}
                    variant={"standard"}
                    label={"Loading..."}
                    noOptionsLabel={"Loading..."}
                    isLoading={isLoading}
                    noMargin={props.noMargin}
                />
            </SelectWrapper>
        );
    };
    return (
        <>
            {props.displayOnlyAsChips ? (
                isLoading || props.isLoading ? (
                    getLoadingBox()
                ) : chosenTags.length === 0 && props.emptyText ? (
                    <DotLegalHeader marginBottom={0} color={"lightgrey"} fontWeight={400} headerStyle={"small"}>
                        {props.emptyText}
                    </DotLegalHeader>
                ) : (
                    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
                        {chosenTags.map((tag) => {
                            return (
                                <Box key={tag.id}>
                                    <DotLegalChip isSelected size="small" key={tag.id} color={tag.color} value={tag.name}></DotLegalChip>
                                </Box>
                            );
                        })}
                    </Box>
                )
            ) : (
                <PlanInputFieldWrapper hasAccess={platformFeatures.tags} hoverText={translateString("upgradePrivacyPlanToday")}>
                    <DotLegalMultiSelectWithButton
                        disabled={props.disabled || !platformFeatures.tags}
                        isLoading={isLoading || props.isLoading}
                        options={selectableTags}
                        chosenOptions={props.selectedTags}
                        onChange={(tags) => onRelationChange(tags)}
                        placeholder={props.placeHolder}
                        label={props.hideLabel ? "" : translateString("tags")}
                        noOptionsLabel={translateString("noOptions")}
                        onButtonClick={() => setShowCreateTagDialog(true)}
                        buttonLabel={translateString("createTag")}
                        hasPermission={permissions.canManageMasterData}
                        onItemAdded={(item) => {
                            if (props.onTagAdded) {
                                props.onTagAdded(item);
                            }
                        }}
                        onItemDeleted={(item) => {
                            if (props.onTagRemoved) {
                                props.onTagRemoved(item);
                            }
                        }}
                        variant={props.variant ?? "outlined"}
                        icon={!platformFeatures.tags ? PurpleLockIcon : undefined}
                        noMargin={props.noMargin}
                        toolTipText={props.tooltipText}
                        errorText={props.errorText}
                        showPopupIconWhenDisabled={!platformFeatures.tags}
                        useFixedLabel={props.useFixedLabel}
                    />
                </PlanInputFieldWrapper>
            )}
            {showCreateTagDialog && (
                <TagDialog onHideDialog={() => setShowCreateTagDialog(false)} onSave={(tagId) => onTagCreated(tagId)} selectedTag={undefined} />
            )}
        </>
    );
}

export default TagsMultiSelect;
